import { Audience } from '../../audience/components/audience.model';
import { DataPlatformAudienceRule } from '../data-platform.model';
import { categoriseAudience } from './audience-guesstimate.utils';
import { audienceCountRules } from './comparable-rules.utils';
import { hasTimeframe, withSingleValue } from './audience-filter-to-dp-rule.utils';
import { CampaignWidgetResolver, EstimateFactor, mapCriteria, matchesEitherSide } from './reach-estimate-factors.utils';

/** Rule types the daily count evaluates; it skips any other (see AudienceQueryService.DailySupportedRuleTypes). */
export const DAILY_SUPPORTED_RULE_TYPES: ReadonlySet<string> = new Set([
  'event_count',
  'goal_count',
  'revenue',
  'last_active',
  'first_seen',
  'total_events',
  'session_count',
  'purchase_count',
  'has_tag',
  'has_consent',
  'has_not_consent',
  'dimensions',
  'dimension_affinity',
  'widget_event_count',
  'is_identified',
  'is_anonymous',
]);

export interface AudienceTrendPlan {
  rules: DataPlatformAudienceRule[];
  /** Criteria the reach estimates from a traffic share; empty when it is exact. */
  factors: EstimateFactor[];
}

/** The reach as daily-count rules plus estimated criteria; null when the daily count can't follow it. */
export function audienceTrendPlan(
  audience: Audience,
  resolveWidgetId?: CampaignWidgetResolver,
): AudienceTrendPlan | null {
  if (audience.isDefault && !audience.filters?.length) {
    const builtIn = defaultAudienceRule(audience.name);
    return builtIn ? { rules: [builtIn], factors: [] } : null;
  }
  if (categoriseAudience(audience) !== 'internal' || matchesEitherSide(audience)) {
    return null;
  }
  const counted = audienceCountRules(audience);
  const { promotedRules, factors, unquantifiable } = mapCriteria(audience, resolveWidgetId);
  const rules = [...counted.rules, ...promotedRules].map(withSingleValue);
  if (!counted.countable || unquantifiable.length || rules.length === 0) {
    return null;
  }
  return rules.every(isDailyRule) ? { rules, factors } : null;
}

/** The rules behind the exact reach; null when the reach is estimated or the daily count would skip one. */
export function exactTrendRules(
  audience: Audience,
  resolveWidgetId?: CampaignWidgetResolver,
): DataPlatformAudienceRule[] | null {
  const plan = audienceTrendPlan(audience, resolveWidgetId);
  return plan?.factors.length === 0 ? plan.rules : null;
}

const DAILY_LIFETIME_RULE_TYPES: ReadonlySet<string> = new Set(['event_count', 'total_events', 'session_count']);

/** The daily count windows purchases, revenue, goals and campaign interactions, has no value lists, coupon-only counts, last active/first seen ranges or interest profile shares, and reads `between` only on revenue. */
function isDailyRule(rule: DataPlatformAudienceRule): boolean {
  const dayRange = (rule.type === 'last_active' || rule.type === 'first_seen') && rule.daysAgoUpper != null;
  const interestShare = rule.type === 'dimension_affinity' && !!rule.dimension?.trim().toLowerCase().startsWith('url:');
  return (
    DAILY_SUPPORTED_RULE_TYPES.has(rule.type.toLowerCase()) &&
    !rule.values?.length &&
    !dayRange &&
    !interestShare &&
    !(DAILY_LIFETIME_RULE_TYPES.has(rule.type) && hasTimeframe(rule)) &&
    !(rule.type === 'purchase_count' && rule.couponOnly) &&
    (rule.operator !== 'between' || rule.type === 'revenue')
  );
}

/**
 * Maps a default-audience name (a translation key like
 * `audience.default.notIdentified`, or a legacy display name) to its DataPlatform rule.
 */
export function defaultAudienceRule(name: string | null | undefined): DataPlatformAudienceRule | null {
  switch (name) {
    case 'audience.default.identified':
    case 'Identified':
    case 'Identifierad':
    case 'Identifierade':
      return { type: 'is_identified' };
    case 'audience.default.notIdentified':
    case 'Not Identified':
    case 'Icke Identifierad':
      return { type: 'is_anonymous' };
    case 'audience.default.consent':
    case 'Has Given Any Consent':
    case 'Har gett något samtycke':
      return { type: 'has_consent' };
    case 'audience.default.withoutConsent':
    case 'Has Not Given Any Consent':
    case 'Har inte gett något samtycke':
      return { type: 'has_not_consent' };
    default:
      return null;
  }
}
